import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, BACKEND_URL } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Navbar } from "@/components/Navbar";
import { adminSignOut } from "@/components/AdminAuthGuard";
import {
  Users, FileWarning, MessageSquare, Star, Wrench, Car, Settings2, X, KeyRound,
  CheckCircle2, XCircle, Eye, Clock, ScrollText, Search, Crown, CreditCard, UserX, Sparkles, Zap, LogOut, FileText, ClipboardList, AlertCircle, ChevronDown,
} from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ShopReviewSheet } from "@/components/ShopReviewSheet";
import ErrorBoundary from "../components/ErrorBoundary";

function usePagination(items, pageSize = 10) {
  const [page, setPage] = useState(1);
  const totalPages = Math.ceil((items?.length || 0) / pageSize);
  const paginated = (items || []).slice((page - 1) * pageSize, page * pageSize);
  // Reset to page 1 when items change
  useEffect(() => { setPage(1); }, [items?.length]);
  return { page, setPage, totalPages, paginated };
}

function Pagination({ page, totalPages, setPage }) {
  if (totalPages <= 1) return null;
  return (
    <div className="flex items-center justify-between px-4 py-3 border-t border-white/10">
      <div className="text-xs text-zinc-500">Page {page} of {totalPages}</div>
      <div className="flex gap-1">
        <button
          onClick={() => setPage(p => Math.max(1, p - 1))}
          disabled={page === 1}
          className="px-3 py-1 text-xs border border-white/10 text-zinc-400 hover:text-white hover:border-white/30 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
        >
          Prev
        </button>
        {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
          let p;
          if (totalPages <= 7) p = i + 1;
          else if (page <= 4) p = i + 1;
          else if (page >= totalPages - 3) p = totalPages - 6 + i;
          else p = page - 3 + i;
          return (
            <button
              key={p}
              onClick={() => setPage(p)}
              className={`px-3 py-1 text-xs border transition-colors ${
                page === p
                  ? 'border-white/30 text-white bg-white/10'
                  : 'border-white/10 text-zinc-400 hover:text-white hover:border-white/30'
              }`}
            >
              {p}
            </button>
          );
        })}
        <button
          onClick={() => setPage(p => Math.min(totalPages, p + 1))}
          disabled={page === totalPages}
          className="px-3 py-1 text-xs border border-white/10 text-zinc-400 hover:text-white hover:border-white/30 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
        >
          Next
        </button>
      </div>
    </div>
  );
}


// ===== Fix 6: Founding Shops Tracking Tab =====
function FoundingShopsTab({ shops, onImpersonate }) {
  // Derive founding shops with calculated status
  const foundingShops = (shops || [])
    .filter((s) => s.founding_days_remaining !== undefined && s.founding_days_remaining !== null)
    .map((s) => {
      const daysRemaining = s.founding_days_remaining;
      // Calculate approval date: premium_until - 120 days (start of founding period)
      const approvalDate = s.premium_until
        ? new Date(new Date(s.premium_until).getTime() - 120 * 24 * 60 * 60 * 1000)
        : s.created_at ? new Date(s.created_at) : null;
      let status, statusColor, statusBg;
      if (daysRemaining <= 0) {
        status = "Expired";
        statusColor = "text-red-400";
        statusBg = "bg-red-500/10";
      } else if (daysRemaining <= 14) {
        status = "Expiring Soon";
        statusColor = "text-yellow-400";
        statusBg = "bg-yellow-500/10";
      } else {
        status = "Active";
        statusColor = "text-emerald-400";
        statusBg = "bg-emerald-500/10";
      }
      return { ...s, approvalDate, daysRemaining, status, statusColor, statusBg };
    })
    .sort((a, b) => a.daysRemaining - b.daysRemaining); // Show expiring soonest first

  const activeCount = foundingShops.filter((s) => s.status === "Active").length;
  const expiringCount = foundingShops.filter((s) => s.status === "Expiring Soon").length;
  const expiredCount = foundingShops.filter((s) => s.status === "Expired").length;

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-[#121214] border border-white/5 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-emerald-400">{activeCount}</div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1">Active</div>
        </div>
        <div className="bg-[#121214] border border-white/5 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-yellow-400">{expiringCount}</div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1">Expiring Soon</div>
        </div>
        <div className="bg-[#121214] border border-white/5 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-red-400">{expiredCount}</div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1">Expired</div>
        </div>
      </div>

      {/* Founding Shops Table */}
      <div className="bg-[#121214] border border-white/5 rounded-lg overflow-hidden">
        <div className="px-5 py-4 border-b border-white/5 flex items-center justify-between">
          <h3 className="text-sm font-bold text-white flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-emerald-400" />
            Founding Shops ({foundingShops.length})
          </h3>
          <span className="text-[10px] text-zinc-500">120-day Premium access tracking</span>
        </div>
        {foundingShops.length === 0 ? (
          <div className="p-8 text-center text-zinc-500 text-sm">No founding shops found.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-white/5 text-[10px] uppercase tracking-widest text-zinc-500">
                  <th className="px-5 py-3">Shop Name</th>
                  <th className="px-5 py-3">Approval Date</th>
                  <th className="px-5 py-3">Days Remaining</th>
                  <th className="px-5 py-3">Premium Until</th>
                  <th className="px-5 py-3">Status</th>
                  <th className="px-5 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {foundingShops.map((s) => (
                  <tr key={s.user_id} className="hover:bg-white/[0.02] transition-colors">
                    <td className="px-5 py-4">
                      <div className="font-bold text-sm text-white">{s.shop_name || s.name}</div>
                      <div className="text-xs text-zinc-500">{s.email}</div>
                    </td>
                    <td className="px-5 py-4 text-xs text-zinc-400 font-mono">
                      {s.approvalDate ? s.approvalDate.toLocaleDateString() : "—"}
                    </td>
                    <td className="px-5 py-4">
                      <span className={`text-sm font-bold font-mono ${s.statusColor}`}>
                        {s.daysRemaining > 0 ? s.daysRemaining : 0}
                      </span>
                      <span className="text-[10px] text-zinc-500 ml-1">days</span>
                    </td>
                    <td className="px-5 py-4 text-xs text-zinc-400 font-mono">
                      {s.premium_until ? new Date(s.premium_until).toLocaleDateString() : "—"}
                    </td>
                    <td className="px-5 py-4">
                      <span className={`text-[10px] font-bold uppercase tracking-tight px-2 py-1 rounded ${s.statusBg} ${s.statusColor}`}>
                        {s.status}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-right">
                      <button onClick={() => onImpersonate(s)} className="text-zinc-500 hover:text-white transition-colors" title="View as shop">
                        <Eye className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const { startImpersonation, startDemoMode } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [issues, setIssues] = useState([]);
  const [demoIssues, setDemoIssues] = useState([]);
  const [pendingShops, setPendingShops] = useState([]);
  const [pendingTotal, setPendingTotal] = useState(0);
  const [pendingPage, setPendingPage] = useState(1);
  const [audit, setAudit] = useState([]);
  const [subscriptions, setSubscriptions] = useState(null);
  const [tab, setTab] = useState("messages");
  const [referralData, setReferralData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionDialog, setActionDialog] = useState(null); // {action, user}
  const [managedUser, setManagedUser] = useState(null);
  const [reviewShopId, setReviewShopId] = useState(null);
  const [unreadMessages, setUnreadMessages] = useState(0);

  const load = async () => {
    try {
      const [s, u, i, p, a, subs, refs, supportConvos, demoRes] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/users"),
        api.get("/admin/issues"),
        api.get("/admin/shops/pending", { params: { skip: 0, limit: 10 } }),
        api.get("/admin/audit-log"),
        api.get("/admin/shops/subscriptions"),
        api.get("/admin/referrals").catch(() => ({ data: null })),
        api.get("/admin/support/conversations").catch(() => ({ data: { conversations: [] } })),
        api.get("/admin/issues", { params: { demo: "only" } }).catch(() => ({ data: [] })),
      ]);
      setStats(s.data);
      setUsers(u.data);
      setIssues(i.data);
      setPendingShops(p.data.shops || []);
      setPendingTotal(p.data.total || 0);
      setAudit(a.data);
      setSubscriptions(subs.data);
      setReferralData(refs.data);
      setDemoIssues(demoRes.data || []);
      const totalUnread = (supportConvos.data?.conversations || []).reduce((acc, c) => acc + (c.unread_admin || 0), 0);
      setUnreadMessages(totalUnread);
    } catch (e) {
      toast.error("Failed to load admin data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    if (loading) return;
    api.get("/admin/shops/pending", { params: { skip: (pendingPage - 1) * 10, limit: 10 } })
      .then(res => {
        setPendingShops(res.data.shops || []);
        setPendingTotal(res.data.total || 0);
      })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingPage]);

  const impersonate = (u) => {
    startImpersonation(u);
    navigate("/");
    toast.success(`Impersonating ${u.email}`);
  };

  const demo = () => {
    startDemoMode();
    navigate("/shop");
    toast.success("Demo session started — you're now the demo shop.", { duration: 4000 });
  };

  const deleteUser = async (id) => {
    if (!window.confirm("Delete user permanently?")) return;
    try {
      await api.delete(`/admin/users/${id}`);
      toast.success("User deleted");
      load();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const toggleTier = async (u) => {
    const next = u.subscription_tier === "free" ? "pro" : "free";
    try {
      await api.post(`/admin/shops/${u.user_id}/tier`, { tier: next });
      toast.success(`User set to ${next}`);
      load();
    } catch (e) {
      toast.error("Failed to update tier");
    }
  };

  if (loading) return <div className="min-h-screen bg-[#09090B] flex items-center justify-center font-display text-[#FF3B30] animate-pulse uppercase tracking-[0.2em] font-black">Accessing Command Center...</div>;

  const approvedShops = users.filter(u => u.role === "shop" && u.status === "approved");

  const tabs = [
    { k: "messages", label: "Support", icon: MessageSquare, count: unreadMessages, accent: unreadMessages > 0 },
    { k: "account", label: "Password", icon: KeyRound },
    { k: "pending", label: "Pending", icon: Clock, count: pendingTotal, accent: true },
    { k: "approved", label: "Approved", icon: CheckCircle2, count: approvedShops.length, accent: false },
    { k: "settings", label: "System", icon: Settings2 },
    { k: "subscriptions", label: "Billing", icon: CreditCard, count: (subscriptions?.totals?.on_trial || 0) + (subscriptions?.totals?.paid_premium || 0) + (subscriptions?.car_owners?.totals?.active || 0) },
    { k: "founding", label: "Founding", icon: Sparkles, count: users.filter(u => u.role === "shop" && u.status === "approved" && u.founding_days_remaining > 0).length, accent: true },
    { k: "users", label: "Users", icon: Users, count: users.length },
    { k: "issues", label: "Leads", icon: FileWarning, count: issues.length },
    { k: "audit", label: "Audit", icon: ScrollText },
    { k: "referrals", label: "Referrals", icon: Crown, count: (referralData?.referrals || []).length },
    { k: "payouts", label: "Payouts", icon: Zap },
    { k: "deactivated", label: "Deactivated", icon: UserX },
  ];

  return (
    <div className="min-h-screen bg-[#09090B] text-white p-4 sm:p-8 lg:p-12">
      <header className="max-w-[1400px] mx-auto flex justify-between items-start mb-12">
        <div>
          <div className="flex items-center gap-3 mb-2 uppercase tracking-[0.3em] text-[10px] font-bold text-zinc-600">
            <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
            Mission Control • System v2.0
          </div>
          <h1 className="font-heading text-4xl sm:text-6xl font-black tracking-tighter">ADMIN<span className="text-[#FF3B30]">DASHBOARD</span></h1>
        </div>
        <div className="flex gap-3">
          <button onClick={demo} className="btn-secondary flex items-center gap-2 py-3 px-6 text-xs uppercase tracking-widest">
            <Sparkles className="w-4 h-4" /> Shop Preview
          </button>
          <button onClick={adminSignOut} className="border border-white/10 hover:bg-white/5 p-3 rounded-none transition-all">
             <LogOut className="w-5 h-5 text-zinc-500" />
          </button>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto">
        {/* Demo Suite — pitch the app live as a shop persona */}

        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 border border-white/10 mb-10">
          <Stat label="USERS" value={stats?.users} icon={Users} />
          <Stat label="OWNERS" value={stats?.owners} icon={Car} />
          <Stat label="APPROVED SHOPS" value={(stats?.shops ?? 0) - (stats?.pending_shops ?? 0)} icon={Wrench} />
          <Stat label="PENDING SHOPS" value={stats?.pending_shops} accent={stats?.pending_shops > 0} icon={Clock} />
        </div>

        {/* shop-status-access-nov2: bookings / accepted jobs with a suspended or rejected shop */}
        <ShopAttentionPanel />

        <div className="mb-6">
          <div className="relative w-full" style={{minWidth: "280px"}}>
            <select
              value={tab}
              onChange={(e) => setTab(e.target.value)}
              data-testid="section-select"
              className="w-full bg-[#1a1a1a] text-white border border-white/20 px-4 py-3 pr-10 text-sm uppercase tracking-widest font-medium cursor-pointer outline-none focus:border-[#FF3B30] transition-colors appearance-none"
            >
              {tabs.map((t) => (
                <option key={t.k} value={t.k} className="bg-[#1a1a1a] text-white">
                  {t.label}{t.count > 0 ? ` (${t.count})` : ""}
                </option>
              ))}
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#FF3B30" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </div>
          </div>
        </div>

        <ErrorBoundary>{tab === "pending" && <PendingTab shops={pendingShops} total={pendingTotal} page={pendingPage} onPageChange={setPendingPage} onReview={(s) => setReviewShopId(s.user_id)} onAction={(a, s) => setActionDialog({ action: a, user: s })} />}</ErrorBoundary>
        <ErrorBoundary>{tab === "approved" && <ApprovedShopsTab shops={approvedShops} onImpersonate={impersonate} onReview={(id) => setReviewShopId(id)} />}</ErrorBoundary>
        <ErrorBoundary>{tab === "subscriptions" && <SubscriptionsTab data={subscriptions} onImpersonate={impersonate} />}</ErrorBoundary>
        <ErrorBoundary>{tab === "founding" && <FoundingShopsTab shops={users.filter(u => u.role === "shop" && u.status === "approved" && (u.founding_days_remaining > 0 || u.founding_days_remaining === 0))} onImpersonate={impersonate} />}</ErrorBoundary>
        <ErrorBoundary>{tab === "users" && <UsersTable users={users} onImpersonate={impersonate} onAction={(a, u) => setActionDialog({ action: a, user: u })} onManage={(u) => setManagedUser(u)} />}</ErrorBoundary>
        <ErrorBoundary>{tab === "issues" && <IssuesTable issues={issues} demoIssues={demoIssues} />}</ErrorBoundary>
        <ErrorBoundary>{tab === "audit" && <AuditTable rows={audit} />}</ErrorBoundary>
        <ErrorBoundary>{tab === "referrals" && <ReferralsTab data={referralData} />}</ErrorBoundary>
        <ErrorBoundary>{tab === "payouts" && <PayoutsTab />}</ErrorBoundary>
        <ErrorBoundary>{tab === "settings" && <SettingsTab />}</ErrorBoundary>
        <ErrorBoundary>{tab === "messages" && <MessagesTab />}</ErrorBoundary>
        <ErrorBoundary>{tab === "account" && <AccountSettingsTab />}</ErrorBoundary>
        <ErrorBoundary>{tab === "deactivated" && <DeactivatedTab />}</ErrorBoundary>

        <ShopActionDialog
          state={actionDialog}
          onClose={() => setActionDialog(null)}
          onSuccess={() => { setActionDialog(null); setReviewShopId(null); load(); }}
        />

        <ShopReviewSheet
          shopUserId={reviewShopId}
          open={!!reviewShopId}
          onOpenChange={(o) => !o && setReviewShopId(null)}
          onAction={(a, s, opts) => setActionDialog({ action: a, user: s, ...opts })}
        />

        {managedUser && <UserDrawer user={managedUser} onClose={() => setManagedUser(null)} onRefresh={load} />}
      </main>
    </div>
  );
}

function Stat({ label, value, accent, icon: Icon }) {
  return (
    <div className="bg-[#09090B] p-6">
      <div className="flex items-center justify-between mb-3">
        <div className="label-eyebrow">{label}</div>
        {Icon && <Icon className="w-4 h-4 text-zinc-600" strokeWidth={1.5} />}
      </div>
      <div className={`font-heading text-4xl font-black ${accent ? "text-[#FF3B30]" : ""}`}>{value ?? 0}</div>
    </div>
  );
}

function PendingTab({ shops, total, page, onPageChange, onReview, onAction }) {
  const submitted = shops.filter((s) => s.application);
  const awaiting = shops.filter((s) => !s.application);
  const totalPages = Math.ceil((total || 0) / 10);

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  if (total === 0 && shops.length === 0) {
    return <div className="border border-dashed border-white/10 p-12 text-center text-zinc-500" data-testid="pending-empty">No shops waiting for approval. 🎉</div>;
  }

  return (
    <div>
      {/* ─── SECTION 1: APPLICATION SUBMITTED (urgent — action required) ─── */}
      <div className="border border-[#FF3B30]/40 bg-gradient-to-b from-[#FF3B30]/[0.06] to-transparent p-6 mb-2">
          <div className="flex items-center gap-3 mb-5">
            <span className="relative flex h-3 w-3 flex-shrink-0">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#FF3B30] opacity-75" />
              <span className="relative inline-flex rounded-full h-3 w-3 bg-[#FF3B30]" />
            </span>
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF3B30]">
              APPLICATION SUBMITTED · {submitted.length}
            </span>
            <span className="text-[10px] font-mono text-[#FF3B30]/50 uppercase tracking-widest">— ACTION REQUIRED</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {submitted.length === 0 && (
              <p className="text-xs text-zinc-500 italic col-span-3 py-2">No application submissions yet — shops that complete their form will appear here.</p>
            )}
            {submitted.map((s) => (
              <div
                key={s.user_id}
                className="border border-[#FF3B30]/30 bg-[#0f0b0b] p-5 flex flex-col hover:border-[#FF3B30]/55 transition-colors"
                data-testid={`pending-${s.user_id}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="w-10 h-10 bg-[#FF3B30]/15 border border-[#FF3B30]/30 flex items-center justify-center">
                    <AlertCircle className="w-5 h-5 text-[#FF3B30]" strokeWidth={2} />
                  </div>
                  <span className="text-[10px] uppercase tracking-widest border border-[#FF3B30]/40 text-[#FF3B30] px-2 py-1 font-bold">
                    APP SUBMITTED
                  </span>
                </div>
                <h3 className="font-heading text-lg font-bold tracking-tight mb-1 text-white">
                  {s.application?.legal_business_name || s.shop_name || "Unnamed Shop"}
                </h3>
                <p className="text-xs text-zinc-400 font-mono mb-1 truncate">{s.email}</p>
                {s.account_number && (
                  <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono mb-1" data-testid={`acct-${s.user_id}`}>
                    ID: {s.account_number}
                  </p>
                )}
                <p className="text-[11px] text-zinc-500 mb-4">
                  Submitted: {formatDate(s.application_submitted_at || s.created_at) || "—"}
                </p>
                <div className="mt-auto">
                  <button
                    onClick={() => onReview(s)}
                    className="w-full py-2.5 bg-[#FF3B30] hover:bg-[#FF3B30]/85 text-white text-xs font-bold uppercase tracking-[0.15em] flex items-center justify-center gap-2 transition-colors"
                    data-testid={`review-${s.user_id}`}
                  >
                    <Eye className="w-3.5 h-3.5" /> REVIEW
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

      {/* ─── VISUAL DIVIDER ─── */}
      {submitted.length > 0 && awaiting.length > 0 && (
        <div className="flex items-center gap-4 py-6">
          <div className="flex-1 h-px bg-white/10" />
          <span className="text-[10px] uppercase tracking-[0.25em] text-zinc-600 font-bold px-2">Not Yet Submitted</span>
          <div className="flex-1 h-px bg-white/10" />
        </div>
      )}

      {/* ─── SECTION 2: AWAITING APPLICATION ─── */}
      {awaiting.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-4">
            <span className="text-[#FF9500]">●</span>
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-400">
              AWAITING APPLICATION · {awaiting.length}
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {awaiting.map((s) => (
              <div key={s.user_id} className="border border-white/10 bg-[#121214] p-6 opacity-70" data-testid={`pending-${s.user_id}`}>
                <div className="flex items-start justify-between mb-4">
                  <div className="w-10 h-10 bg-zinc-800 flex items-center justify-center">
                    <Clock className="w-5 h-5 text-zinc-500" strokeWidth={2.5} />
                  </div>
                  <span className="text-[10px] uppercase tracking-widest bg-[#FF9500] text-black px-2 py-1">NO APP</span>
                </div>
                <h3 className="font-heading text-xl font-bold tracking-tight mb-1">{s.shop_name || "Unnamed Shop"}</h3>
                {s.created_at && <p className="text-[11px] text-zinc-500 mb-1">Registered: {formatDate(s.created_at)}</p>}
                <p className="text-sm text-zinc-400 mb-1">{s.name}</p>
                <p className="text-xs text-zinc-500 font-mono mb-1 truncate">{s.email}</p>
                {s.account_number && (
                  <p className="text-[10px] uppercase tracking-widest text-zinc-600 mb-3" data-testid={`acct-${s.user_id}`}>
                    {s.account_number}
                  </p>
                )}
                <p className="text-xs text-zinc-500 italic">Cannot approve until shop submits their application.</p>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-6">
        <Pagination page={page} totalPages={totalPages} setPage={onPageChange} />
      </div>
    </div>
  );
}

function SubscriptionsTab({ data, onImpersonate }) {
  const paidRows = [...(data?.paid_premium || []), ...(data?.on_trial || [])];
  const freeRows = data?.free || [];

  const { page: paidPage, setPage: setPaidPage, totalPages: paidTotalPages, paginated: paidPaginated } = usePagination(paidRows, 10);
  const { page: freePage, setPage: setFreePage, totalPages: freeTotalPages, paginated: freePaginated } = usePagination(freeRows, 10);

  if (!data) return <div className="text-zinc-500 text-sm p-4">Loading subscriptions...</div>;

  const totals = data.totals || {};
  const carOwners = data.car_owners || { active: [], totals: {} };

  const STAT_CARDS = [
    { label: "On Trial", value: totals.on_trial ?? 0, color: "text-blue-400" },
    { label: "Paid Premium", value: totals.paid_premium ?? 0, color: "text-emerald-400" },
    { label: "Free", value: totals.free ?? 0, color: "text-zinc-400" },
    { label: "Just Signed Up (30d)", value: totals.just_signed_up ?? 0, color: "text-amber-400" },
    { label: "Never Signed Up", value: totals.never_signed_up ?? 0, color: "text-red-400" },
    { label: "Total Shops", value: totals.approved_shops ?? 0, color: "text-white" },
  ];

  const ShopRow = ({ s, cohort }) => (
    <tr key={s.user_id} className="hover:bg-white/[0.01] transition-colors">
      <td className="p-4">
        <div className="font-bold text-sm">{s.shop_name || s.name}</div>
        <div className="text-xs text-zinc-500">{s.email}</div>
      </td>
      <td className="p-4">
        <span className={`text-[10px] font-bold uppercase tracking-tighter px-2 py-1 ${
          cohort === "paid_premium" ? "bg-emerald-500/10 text-emerald-400" :
          cohort === "on_trial"    ? "bg-blue-500/10 text-blue-400" :
                                     "bg-zinc-500/10 text-zinc-400"
        }`}>{cohort === "paid_premium" ? "Paid" : cohort === "on_trial" ? "Trial" : "Free"}</span>
      </td>
      <td className="p-4">
        <div className="flex flex-col gap-1">
          <span className={`text-[10px] font-bold uppercase px-2 py-1 inline-block w-fit ${s.subscription_tier !== "free" ? "bg-amber-500/10 text-amber-400" : "bg-zinc-700/40 text-zinc-500"}`}>
            {s.subscription_tier || "free"}
          </span>
          {s.founding_days_remaining > 0 && (
            <span className="text-[10px] font-bold px-2 py-0.5 bg-emerald-500/10 text-emerald-400 inline-block w-fit">
              Founding — {s.founding_days_remaining} days remaining
            </span>
          )}
        </div>
      </td>
      <td className="p-4 text-xs text-zinc-400 font-mono">
        {s.premium_until ? new Date(s.premium_until).toLocaleDateString() : "—"}
      </td>
      <td className="p-4 text-xs text-zinc-500 font-mono">
        {s.last_paid_at ? new Date(s.last_paid_at).toLocaleDateString() : "—"}
      </td>
      <td className="p-4 text-right">
        <button onClick={() => onImpersonate(s)} className="text-zinc-500 hover:text-white transition-colors">
          <Eye className="w-4 h-4" />
        </button>
      </td>
    </tr>
  );

  return (
    <div className="space-y-6">
      {/* Cohort Summary Cards */}
      <div className="grid grid-cols-3 gap-3">
        {STAT_CARDS.map(({ label, value, color }) => (
          <div key={label} className="border border-white/10 bg-[#121214] p-4 rounded">
            <div className={`text-2xl font-black font-mono ${color}`}>{value}</div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1">{label}</div>
          </div>
        ))}
      </div>

      {/* Owner subscriptions are tracked separately from car repair shops. */}
      <div className="border border-white/10 bg-[#121214] p-4 space-y-4">
        <h3 className="text-sm font-bold text-amber-400 uppercase tracking-widest">Car Owner Billing</h3>
        <div className="grid grid-cols-3 gap-3">
          {[
            ["Owner Premium", carOwners.totals?.active || 0],
            ["On Trial", carOwners.totals?.on_trial || 0],
            ["Paid", carOwners.totals?.paid || 0],
          ].map(([label, value]) => (
            <div key={label} className="border border-white/10 p-3 rounded">
              <div className="text-2xl font-bold font-mono">{value}</div>
              <div className="text-xs text-zinc-400">{label}</div>
            </div>
          ))}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead><tr className="border-b border-white/10 text-zinc-400 text-xs uppercase"><th className="p-3">Owner</th><th className="p-3">Status</th><th className="p-3">Started</th><th className="p-3">Premium Until</th></tr></thead>
            <tbody className="divide-y divide-white/5">
              {carOwners.active?.length ? carOwners.active.map(o => (
                <tr key={o.user_id}><td className="p-3"><div>{o.name}</div><div className="text-zinc-500 text-xs">{o.email}</div></td><td className="p-3 capitalize">{o.subscription_status}</td><td className="p-3">{o.started_at ? new Date(o.started_at).toLocaleDateString() : "—"}</td><td className="p-3">{o.premium_until ? new Date(o.premium_until).toLocaleDateString() : "—"}</td></tr>
              )) : <tr><td colSpan={4} className="p-4 text-zinc-500 text-xs">No active car owner subscribers yet</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* Active Subscribers Table (Paid + Trial) */}
      <div className="border border-white/10 bg-[#121214] overflow-x-auto">
        <div className="px-4 py-3 border-b border-white/10 text-[10px] uppercase tracking-widest text-zinc-500 font-bold">Active Subscribers</div>
        <table className="w-full text-left">
          <thead>
            <tr className="border-b border-white/10 bg-white/[0.02] uppercase tracking-widest text-[10px] font-bold text-zinc-500">
              <th className="p-4">Shop</th>
              <th className="p-4">Cohort</th>
              <th className="p-4">Tier</th>
              <th className="p-4">Premium Until</th>
              <th className="p-4">Last Paid</th>
              <th className="p-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {paidRows.length === 0 ? (
              <tr><td colSpan={6} className="p-8 text-center text-zinc-600 text-xs">No active subscribers yet</td></tr>
            ) : (
              paidPaginated.map((s) => <ShopRow key={s.user_id} s={s} cohort={s.subscription_status === "trialing" || (data.on_trial || []).some(t => t.user_id === s.user_id) ? "on_trial" : "paid_premium"} />)
            )}
          </tbody>
        </table>
        <Pagination page={paidPage} totalPages={paidTotalPages} setPage={setPaidPage} />
      </div>

      {/* Free / Lapsed Shops */}
      {freeRows.length > 0 && (
        <div className="border border-white/10 bg-[#121214] overflow-x-auto">
          <div className="px-4 py-3 border-b border-white/10 text-[10px] uppercase tracking-widest text-zinc-500 font-bold">Free / Lapsed ({freeRows.length})</div>
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-white/10 bg-white/[0.02] uppercase tracking-widest text-[10px] font-bold text-zinc-500">
                <th className="p-4">Shop</th>
                <th className="p-4">Cohort</th>
                <th className="p-4">Tier</th>
                <th className="p-4">Premium Until</th>
                <th className="p-4">Last Paid</th>
                <th className="p-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {freePaginated.map((s) => <ShopRow key={s.user_id} s={s} cohort="free" />)}
            </tbody>
          </table>
          <Pagination page={freePage} totalPages={freeTotalPages} setPage={setFreePage} />
        </div>
      )}
    </div>
  );
}

function UsersTable({ users, onImpersonate, onAction, onManage }) {
  const [query, setQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");

  const filtered = users.filter((u) => {
    if (roleFilter !== "all" && u.role !== roleFilter) return false;
    if (!query.trim()) return true;
    const q = query.trim().toLowerCase();
    return (
      (u.account_number || "").toLowerCase().includes(q) ||
      (u.email || "").toLowerCase().includes(q) ||
      (u.name || "").toLowerCase().includes(q) ||
      (u.shop_name || "").toLowerCase().includes(q) ||
      (u.user_id || "").toLowerCase().includes(q)
    );
  });

  const { page, setPage, totalPages, paginated } = usePagination(filtered, 10);

  return (
    <div className="space-y-4">
      {/* Search bar */}
      <div className="border border-white/10 bg-[#121214] p-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[300px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name, email, or account #..."
            className="w-full bg-black/50 border border-white/10 pl-10 pr-4 py-2 text-sm text-white placeholder:text-zinc-600 focus:border-[#FF3B30]/50 outline-none"
          />
        </div>
        <select
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
          className="bg-[#121214] border border-white/10 text-xs text-zinc-400 px-4 py-2 uppercase tracking-widest outline-none focus:border-[#FF3B30]/50"
        >
          <option value="all">All Roles</option>
          <option value="owner">Owners</option>
          <option value="shop">Shops</option>
          <option value="admin">Admins</option>
        </select>
      </div>

      <div className="border border-white/10 bg-[#121214] overflow-x-auto">
        <table className="w-full text-left">
          <thead>
            <tr className="border-b border-white/10 bg-white/[0.02] uppercase tracking-widest text-[10px] font-bold text-zinc-500">
              <th className="p-4">Account</th>
              <th className="p-4">Identity</th>
              <th className="p-4">Shop Name</th>
              <th className="p-4">Rating</th>
              <th className="p-4">Joined</th>
              <th className="p-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {paginated.map((u) => (
              <tr key={u.user_id} className="hover:bg-white/[0.01] transition-colors">
                <td className="p-4">
                  <div className="font-mono text-xs text-zinc-300">{u.account_number || "..."}</div>
                  <div className="text-[10px] font-mono text-zinc-600 mt-1 uppercase">{u.role} | {u.subscription_tier}</div>
                </td>
                <td className="p-4">
                  <div className="font-bold text-sm text-[#E2E8F0]">{u.name}</div>
                  <div className="text-xs text-zinc-500">{u.email}</div>
                </td>
                <td className="p-4">
                   {u.role === 'shop' ? (
                     <div className="text-sm font-medium">{u.shop_name || "—"}</div>
                   ) : (
                     <span className="text-zinc-700 font-mono text-xs">N/A</span>
                   )}
                </td>
                <td className="p-4">
                   {u.role === 'shop' ? (
                      <div className="flex items-center gap-1 text-xs">
                         <Star className="w-3 h-3 text-amber-500 fill-amber-500" />
                         <span>{u.rating_avg || 0}</span>
                         <span className="text-zinc-600 font-mono">({u.rating_count || 0})</span>
                      </div>
                   ) : "—"}
                </td>
                <td className="p-4 text-xs text-zinc-500 font-mono">
                   {new Date(u.created_at).toLocaleDateString()}
                </td>
                <td className="p-4 text-right">
                   <div className="flex justify-end gap-2">
                     <button onClick={() => onImpersonate(u)} title="Impersonate" className="p-2 text-zinc-500 hover:text-white transition-all">
                       <Eye className="w-4 h-4" />
                     </button>
                     <button onClick={() => onManage(u)} title="Manage User" className="p-2 text-zinc-500 hover:text-white transition-all">
                       <Settings2 className="w-4 h-4" />
                     </button>
                   </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <Pagination page={page} totalPages={totalPages} setPage={setPage} />
      </div>
    </div>
  );
}

function UserDrawer({ user, onClose, onRefresh }) {
  const [tier, setTier] = useState(user?.subscription_tier || "free");
  const [saving, setSaving] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  if (!user) return null;

  const doTierSave = async () => {
    setShowConfirm(false);
    setSaving(true);
    try {
      await api.post(`/admin/shops/${user.user_id}/tier`, { tier });
      toast.success(`Tier updated to ${tier}`);
      onRefresh();
    } catch {
      toast.error("Failed to update tier");
    } finally { setSaving(false); }
  };

  const handleStatus = async (action) => {
    const confirmMsg = action === "delete"
      ? `Permanently delete ${user.name || user.email}? This cannot be undone.`
      : `${action.charAt(0).toUpperCase() + action.slice(1)} ${user.name || user.email}?`;
    if (!window.confirm(confirmMsg)) return;
    // shop-status-access-nov2: the suspension reason is shown to the shop.
    let reason;
    if (action === "suspend" && user.role === "shop") {
      const answer = window.prompt("Suspension reason (shown to the shop):", "");
      // shop-status-fixes-nov2 (L6): Cancel (null) aborts; nothing is suspended.
      if (answer === null) return;
      reason = answer.trim();
    }
    setSaving(true);
    try {
      if (action === "delete") {
        await api.delete(`/admin/users/${user.user_id}`);
        toast.success("User deleted");
        onClose();
      } else {
        await api.post(`/admin/users/${user.user_id}/status`, reason !== undefined ? { action, reason } : { action });
        toast.success(`User ${action}d`);
      }
      onRefresh();
    } catch {
      toast.error(`Failed to ${action} user`);
    } finally { setSaving(false); }
  };

  const statusBadge = {
    active: "text-green-400 bg-green-400/10",
    suspended: "text-amber-400 bg-amber-400/10",
    deactivated: "text-red-400 bg-red-400/10",
  }[user.status] || "text-zinc-400 bg-zinc-400/10";

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/60 z-40" onClick={onClose} />
      {/* Drawer */}
      <div className="fixed top-0 right-0 h-full w-full max-w-md bg-[#0D0D0F] border-l border-white/10 z-50 flex flex-col overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <div>
            <p className="text-xs text-zinc-500 uppercase tracking-widest font-bold">Manage User</p>
            <p className="text-lg font-bold text-white mt-1">{user.name || "—"}</p>
            <p className="text-xs text-zinc-400">{user.email}</p>
          </div>
          <button onClick={onClose} className="p-2 text-zinc-500 hover:text-white transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* User Info */}
        <div className="p-6 border-b border-white/10 space-y-3">
          <div className="flex justify-between text-sm">
            <span className="text-zinc-500">Account</span>
            <span className="font-mono text-zinc-300">{user.account_number || "—"}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-zinc-500">Role</span>
            <span className="text-zinc-300 uppercase text-xs tracking-widest">{user.role}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-zinc-500">Status</span>
            <span className={`text-xs px-2 py-0.5 font-bold uppercase tracking-widest ${statusBadge}`}>{user.status || "active"}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-zinc-500">Joined</span>
            <span className="text-zinc-400 font-mono text-xs">{new Date(user.created_at).toLocaleDateString()}</span>
          </div>
          {user.role === "shop" && (
            <div className="flex justify-between text-sm">
              <span className="text-zinc-500">Shop</span>
              <span className="text-zinc-300">{user.shop_name || "—"}</span>
            </div>
          )}
        </div>

        {/* Tier Management */}
        <div className="p-6 border-b border-white/10">
          <p className="text-xs font-bold text-zinc-500 uppercase tracking-widest mb-4">Subscription Tier</p>
          <div className="flex gap-2 mb-4">
            {(user?.role === "owner" ? ["free", "pro"] : ["free", "pro", "premium"]).map((t) => (
              <button
                key={t}
                onClick={() => setTier(t)}
                className={`flex-1 py-2 text-xs font-bold uppercase tracking-widest border transition-colors ${
                  tier === t
                    ? "border-[#FFB800] text-[#FFB800] bg-[#FFB800]/10"
                    : "border-white/10 text-zinc-500 hover:border-white/30"
                }`}
              >
                {t}
              </button>
            ))}
          </div>
          <button
            onClick={() => setShowConfirm(true)}
            disabled={saving || tier === user.subscription_tier}
            className="w-full py-2.5 text-xs font-bold uppercase tracking-widest bg-[#FFB800] text-black hover:bg-[#FFB800]/90 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            {saving ? "Saving..." : "Save Tier Change"}
          </button>
          {showConfirm && (
            <div className="mt-3 border border-[#FFB800]/30 bg-[#FFB800]/5 p-4 space-y-3">
              <p className="text-xs text-zinc-300 leading-relaxed">
                Change <span className="font-bold text-white">{user.shop_name || user.name || user.email}</span> from{" "}
                <span className="font-bold text-amber-400 uppercase">{user.subscription_tier || "free"}</span> to{" "}
                <span className="font-bold text-[#FFB800] uppercase">{tier}</span>?{" "}
                This will update their lead access immediately.
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowConfirm(false)}
                  className="flex-1 py-2 text-xs font-bold uppercase tracking-widest border border-white/10 text-zinc-400 hover:border-white/30 hover:text-white transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={doTierSave}
                  className="flex-1 py-2 text-xs font-bold uppercase tracking-widest bg-[#FFB800] text-black hover:bg-[#FFB800]/90 transition-colors"
                >
                  Confirm
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Status Actions */}
        <div className="p-6 border-b border-white/10">
          <p className="text-xs font-bold text-zinc-500 uppercase tracking-widest mb-4">Account Status</p>
          <div className="space-y-2">
            <button
              onClick={() => handleStatus("deactivate")}
              disabled={saving || user.status === "deactivated"}
              className="w-full py-2.5 text-xs font-bold uppercase tracking-widest border border-white/10 text-zinc-400 hover:border-white/30 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              Deactivate Account
            </button>
            <button
              onClick={() => handleStatus("reactivate")}
              disabled={saving || user.status === "active"}
              className="w-full py-2.5 text-xs font-bold uppercase tracking-widest border border-green-500/30 text-green-400 hover:bg-green-500/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              Reactivate Account
            </button>
          </div>
        </div>

        {/* Danger Zone */}
        <div className="p-6">
          <p className="text-xs font-bold text-red-500/70 uppercase tracking-widest mb-4">Danger Zone</p>
          <button
            onClick={() => handleStatus("delete")}
            disabled={saving}
            className="w-full py-2.5 text-xs font-bold uppercase tracking-widest border border-red-500/30 text-red-400 hover:bg-red-500/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            Permanently Delete User
          </button>
        </div>
      </div>
    </>
  );
}

function IssuesTable({ issues, demoIssues = [] }) {
  const { page, setPage, totalPages, paginated } = usePagination(issues, 10);
  const [showDemo, setShowDemo] = useState(false);
  return (
    <div className="space-y-6">
      {/* Real Leads */}
      <div className="border border-white/10 bg-[#121214] overflow-x-auto">
        <table className="w-full text-left">
          <thead>
            <tr className="border-b border-white/10 bg-white/[0.02] uppercase tracking-widest text-[10px] font-bold text-zinc-500">
              <th className="p-4">Vehicle</th>
              <th className="p-4">Description</th>
              <th className="p-4">Bids</th>
              <th className="p-4">Status</th>
              <th className="p-4 text-right">Posted</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {paginated.map((i) => (
              <tr key={i.issue_id || i.id} className="hover:bg-white/[0.01] transition-colors">
                <td className="p-4">
                  <div className="font-bold text-sm">{i.vehicle_year || i.car_year} {i.vehicle_make || i.car_make} {i.vehicle_model || i.car_model}</div>
                  <div className="text-[10px] font-mono text-zinc-500">{i.issue_id || i.id}</div>
                </td>
                <td className="p-4">
                  <div className="text-xs text-zinc-400 line-clamp-1 max-w-xs">{i.description}</div>
                </td>
                <td className="p-4">
                  <div className="text-xs">{i.bid_count || 0}</div>
                </td>
                <td className="p-4">
                  <span className={`text-[10px] font-bold uppercase px-2 py-0.5 border ${i.status === 'open' ? 'border-emerald-500/30 text-emerald-500' : 'border-zinc-500/30 text-zinc-500'}`}>
                    {i.status}
                  </span>
                </td>
                <td className="p-4 text-right text-xs text-zinc-500 font-mono">
                  {new Date(i.created_at).toLocaleDateString()}
                </td>
              </tr>
            ))}
            {paginated.length === 0 && (
              <tr><td colSpan={5} className="p-8 text-center text-zinc-500 text-sm">No real leads yet</td></tr>
            )}
          </tbody>
        </table>
        <Pagination page={page} totalPages={totalPages} setPage={setPage} />
      </div>

      {/* Demo/Test Leads (collapsed by default) */}
      {demoIssues.length > 0 && (
        <div className="border border-amber-500/20 bg-amber-500/[0.03] rounded">
          <button
            onClick={() => setShowDemo(!showDemo)}
            className="w-full flex items-center justify-between p-3 text-left hover:bg-amber-500/[0.05] transition-colors"
          >
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-bold uppercase px-2 py-0.5 border border-amber-500/40 text-amber-500 bg-amber-500/10 rounded">TEST</span>
              <span className="text-xs text-amber-400 font-medium">Demo Leads ({demoIssues.length})</span>
            </div>
            <ChevronDown className={`w-4 h-4 text-amber-500 transition-transform ${showDemo ? 'rotate-180' : ''}`} />
          </button>
          {showDemo && (
            <div className="border-t border-amber-500/20 overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-amber-500/10 bg-amber-500/[0.02] uppercase tracking-widest text-[10px] font-bold text-amber-600">
                    <th className="p-3">Vehicle</th>
                    <th className="p-3">Description</th>
                    <th className="p-3">Status</th>
                    <th className="p-3 text-right">Posted</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-amber-500/10">
                  {demoIssues.map((i) => (
                    <tr key={i.issue_id || i.id} className="hover:bg-amber-500/[0.03] transition-colors">
                      <td className="p-3">
                        <div className="font-bold text-sm text-amber-300">{i.vehicle_year || i.car_year} {i.vehicle_make || i.car_make} {i.vehicle_model || i.car_model}</div>
                        <div className="text-[10px] font-mono text-amber-600">{i.issue_id || i.id}</div>
                      </td>
                      <td className="p-3">
                        <div className="text-xs text-amber-400/70 line-clamp-1 max-w-xs">{i.description}</div>
                      </td>
                      <td className="p-3">
                        <span className="text-[10px] font-bold uppercase px-2 py-0.5 border border-amber-500/30 text-amber-500">
                          {i.status}
                        </span>
                      </td>
                      <td className="p-3 text-right text-xs text-amber-600 font-mono">
                        {new Date(i.created_at).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function AuditTable({ rows }) {
  const { page, setPage, totalPages, paginated } = usePagination(rows, 10);
  return (
    <div className="border border-white/10 bg-[#121214] overflow-x-auto">
      <table className="w-full text-left">
        <thead>
          <tr className="border-b border-white/10 bg-white/[0.02] uppercase tracking-widest text-[10px] font-bold text-zinc-500">
            <th className="p-4">Action</th>
            <th className="p-4">User</th>
            <th className="p-4">Details</th>
            <th className="p-4 text-right">Timestamp</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-white/5">
          {paginated.map((r, idx) => (
            <tr key={idx} className="text-xs">
              <td className="p-4">
                <span className="font-mono uppercase text-[#FF3B30]">{r.action}</span>
              </td>
              <td className="p-4">{r.email}</td>
              <td className="p-4 text-zinc-400">{r.details}</td>
              <td className="p-4 text-right text-zinc-500 font-mono">
                {new Date(r.timestamp).toLocaleString()}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <Pagination page={page} totalPages={totalPages} setPage={setPage} />
    </div>
  );
}

function TaxDocumentReview() {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [denyInputId, setDenyInputId] = useState(null);
  const [denyReason, setDenyReason] = useState("");

  const loadSubmissions = async () => {
    try {
      const res = await api.get("/admin/tax-submissions");
      setSubmissions(res.data || []);
    } catch (e) {
      console.error("Failed to load tax submissions", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadSubmissions(); }, []);

  const handleApprove = async (submissionId) => {
    setActionLoading(submissionId);
    try {
      await api.post(`/admin/tax-submissions/${submissionId}/approve`);
      toast.success("Tax submission approved");
      loadSubmissions();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to approve");
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeny = async (submissionId) => {
    if (!denyReason.trim()) {
      toast.error("Please provide a reason for denial");
      return;
    }
    setActionLoading(submissionId);
    try {
      await api.post(`/admin/tax-submissions/${submissionId}/deny`, { reason: denyReason.trim() });
      toast.success("Tax submission denied");
      setDenyInputId(null);
      setDenyReason("");
      loadSubmissions();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to deny");
    } finally {
      setActionLoading(null);
    }
  };

  const pendingCount = submissions.filter(s => s.status === "pending").length;

  if (loading) return <div className="p-6 text-center text-zinc-500 text-xs italic">Loading tax submissions...</div>;

  return (
    <div className="mb-8">
      <div className="flex items-center gap-3 mb-4">
        <h3 className="text-sm font-bold uppercase tracking-widest text-zinc-400">Tax Document Review</h3>
        {pendingCount > 0 && (
          <span className="text-[10px] font-bold bg-amber-500/10 text-amber-400 border border-amber-500/30 px-2 py-0.5">{pendingCount} PENDING</span>
        )}
      </div>
      {submissions.length === 0 ? (
        <div className="border border-white/5 bg-[#121214] p-6 text-center text-zinc-600 text-xs">
          No pending tax submissions
        </div>
      ) : (
        <div className="space-y-3">
          {submissions.map(s => (
            <div key={s.submission_id} className="border border-white/10 bg-[#121214] p-4">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                <div className="space-y-1 flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-bold text-white">{s.legal_name}</span>
                    <span className={`text-[10px] font-bold uppercase px-2 py-0.5 border ${
                      s.status === "pending" ? "border-amber-500/30 text-amber-400 bg-amber-500/5" :
                      s.status === "approved" ? "border-emerald-500/30 text-emerald-400 bg-emerald-500/5" :
                      "border-red-500/30 text-red-400 bg-red-500/5"
                    }`}>{s.status}</span>
                  </div>
                  <div className="text-xs text-zinc-500">{s.user_email}</div>
                  <div className="text-xs text-zinc-400 font-mono">
                    {s.tax_id_type?.toUpperCase()} {s.ssn_or_ein ?? `••••${s.tax_id_last4}`}
                  </div>
                  <div className="text-xs text-zinc-500">
                    {s.mailing_address?.street}, {s.mailing_address?.city}, {s.mailing_address?.state} {s.mailing_address?.zip}
                  </div>
                  <div className="text-[10px] text-zinc-600 font-mono">
                    Submitted: {s.submitted_at ? new Date(s.submitted_at).toLocaleString() : "—"}
                  </div>
                  {s.gov_id_photo_url && (
                    <button
                      type="button"
                      onClick={() => {
                        const url = s.gov_id_photo_url;
                        if (url.startsWith("http://") || url.startsWith("https://")) {
                          window.open(url, "_blank");
                        } else {
                          const token = localStorage.getItem("auth_token");
                          const full = `${BACKEND_URL}/api/files/${url}${token ? `?auth=${encodeURIComponent(token)}` : ""}`;
                          window.open(full, "_blank");
                        }
                      }}
                      className="text-[10px] text-blue-400 hover:text-blue-300 underline cursor-pointer"
                    >
                      View ID
                    </button>
                  )}
                  {s.deny_reason && (
                    <div className="text-[10px] text-red-400">Denial reason: {s.deny_reason}</div>
                  )}
                </div>
                {s.status === "pending" && (
                  <div className="flex flex-col gap-2 shrink-0">
                    <button
                      onClick={() => handleApprove(s.submission_id)}
                      disabled={actionLoading === s.submission_id}
                      className="px-3 py-1.5 text-xs font-bold uppercase tracking-widest border border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10 transition-colors disabled:opacity-40"
                    >
                      Approve
                    </button>
                    {denyInputId === s.submission_id ? (
                      <div className="flex flex-col gap-1">
                        <input
                          type="text"
                          value={denyReason}
                          onChange={e => setDenyReason(e.target.value)}
                          placeholder="Reason for denial..."
                          className="bg-[#09090B] border border-white/10 px-2 py-1 text-xs text-white placeholder-zinc-600 focus:outline-none focus:border-white/30 w-full"
                          autoFocus
                        />
                        <div className="flex gap-1">
                          <button
                            onClick={() => handleDeny(s.submission_id)}
                            disabled={actionLoading === s.submission_id || !denyReason.trim()}
                            className="px-2 py-1 text-[10px] font-bold uppercase border border-red-500/40 text-red-400 hover:bg-red-500/10 disabled:opacity-40 flex-1"
                          >
                            Confirm Deny
                          </button>
                          <button
                            onClick={() => { setDenyInputId(null); setDenyReason(""); }}
                            className="px-2 py-1 text-[10px] text-zinc-500 border border-white/10 hover:text-white"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button
                        onClick={() => setDenyInputId(s.submission_id)}
                        className="px-3 py-1.5 text-xs font-bold uppercase tracking-widest border border-red-500/40 text-red-400 hover:bg-red-500/10 transition-colors"
                      >
                        Deny
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ReferralsTab({ data }) {
  const { page, setPage, totalPages, paginated } = usePagination(data?.referrals || [], 10);
  if (!data) return <div className="p-12 text-center text-zinc-500 italic">Scanning referral network...</div>;
  return (
    <div className="space-y-8">
      {/* Tax Document Review — at the TOP */}
      <TaxDocumentReview />

      {/* Existing referrals table + stats */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
         <div className="lg:col-span-2 border border-white/10 bg-[#121214] overflow-hidden">
            <table className="w-full text-left">
              <thead>
                 <tr className="border-b border-white/10 bg-white/[0.02] uppercase tracking-widest text-[10px] font-bold text-zinc-500">
                   <th className="p-4">Referrer</th>
                   <th className="p-4">Referee</th>
                   <th className="p-4">Status</th>
                   <th className="p-4 text-right">Date</th>
                 </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {paginated.map((l, idx) => (
                  <tr key={idx} className="text-xs">
                    <td className="p-4 font-bold">{l.referrer_email}</td>
                    <td className="p-4">{l.referee_email}</td>
                    <td className="p-4">
                      <span className={`text-[10px] font-bold uppercase px-2 py-0.5 border ${l.status === 'converted' ? 'border-emerald-500/30 text-emerald-500' : 'border-zinc-500/30 text-zinc-500'}`}>
                        {l.status}
                      </span>
                    </td>
                    <td className="p-4 text-right text-zinc-500 font-mono">
                      {l.referred_at ? new Date(l.referred_at).toLocaleDateString() : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Pagination page={page} totalPages={totalPages} setPage={setPage} />
         </div>
         <div className="space-y-6">
            <div className="border border-white/10 bg-[#121214] p-6">
               <h3 className="text-sm font-bold uppercase tracking-widest mb-4">Referral Stats</h3>
               <div className="space-y-4">
                  <div className="flex justify-between items-center">
                     <span className="text-xs text-zinc-500">Total Invites</span>
                     <span className="font-bold">{data.total_referrals || 0}</span>
                  </div>
                  <div className="flex justify-between items-center">
                     <span className="text-xs text-zinc-500">Conversions</span>
                     <span className="font-bold text-emerald-500">{data.converted_referrals || 0}</span>
                  </div>
                  <div className="flex justify-between items-center pt-4 border-t border-white/5">
                     <span className="text-xs text-zinc-500">Conv. Rate</span>
                     <span className="font-bold text-[#FF3B30]">{data.conversion_rate ? (data.conversion_rate * 100).toFixed(1) : 0}%</span>
                  </div>
               </div>
            </div>
         </div>
      </div>
    </div>
  );
}

function PayoutsTab() {
  const [subTab, setSubTab] = useState("pending");
  const [summary, setSummary] = useState(null);
  const [payouts, setPayouts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);

  const loadSummary = async () => {
    try {
      const res = await api.get("/admin/payouts/summary");
      setSummary(res.data);
    } catch (e) {
      console.error("Failed to load payout summary", e);
    }
  };

  const loadPayouts = async (status) => {
    setLoading(true);
    try {
      const params = status === "pending" ? { status: "pending_payout" } : { status: "paid" };
      // For history, also include failed
      if (status === "history") {
        const [paidRes, failedRes] = await Promise.all([
          api.get("/admin/payouts", { params: { status: "paid", limit: 200 } }),
          api.get("/admin/payouts", { params: { status: "failed", limit: 200 } }),
        ]);
        const combined = [...(paidRes.data.payouts || []), ...(failedRes.data.payouts || [])];
        combined.sort((a, b) => new Date(b.paid_at || b.failed_at || b.created_at) - new Date(a.paid_at || a.failed_at || a.created_at));
        setPayouts(combined);
        setTotal(combined.length);
      } else {
        const [pendingRes, requestedRes] = await Promise.all([
          api.get("/admin/payouts", { params: { status: "pending_payout", limit: 200 } }),
          api.get("/admin/payouts", { params: { status: "payout_requested", limit: 200 } }),
        ]);
        const combined = [
          ...(requestedRes.data?.payouts || []).map(e => ({ ...e, _is_requested: true })),
          ...(pendingRes.data?.payouts || []),
        ];
        setPayouts(combined);
        setTotal(combined.length);
      }
    } catch (e) {
      toast.error("Failed to load payouts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSummary();
    loadPayouts(subTab);
  }, [subTab]);

  const handleApprove = async (earningId) => {
    setActionLoading(earningId);
    try {
      await api.post("/admin/payouts/trigger", { earning_id: earningId, note: "Approved via admin dashboard" });
      toast.success("Payout marked as paid");
      loadPayouts(subTab);
      loadSummary();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to approve payout");
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (earningId) => {
    setActionLoading(earningId);
    try {
      await api.post("/admin/payouts/mark-failed", { earning_id: earningId, note: "Rejected via admin dashboard" });
      toast.success("Payout marked as failed");
      loadPayouts(subTab);
      loadSummary();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to reject payout");
    } finally {
      setActionLoading(null);
    }
  };

  const exportCSV = () => {
    if (!payouts.length) return toast.error("No data to export");
    const headers = ["Referrer", "Email", "Amount", "Status", "Date", "Method", "Note"];
    const rows = payouts.map((p) => [
      p.referrer_shop_name || "Unknown",
      p.referrer_email || "",
      p.amount || 0,
      p.payout_status || p.status || "unknown",
      p.paid_at || p.failed_at || p.created_at || "",
      p.payout_method || "manual",
      p.payout_note || "",
    ]);
    const csv = [headers.join(","), ...rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `payouts-${subTab}-${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("CSV exported");
  };

  const statusBadge = (status) => {
    const map = {
      pending_payout: "border-amber-500/30 text-amber-500",
      payout_requested: "border-[#FFB800]/30 text-[#FFB800]",
      paid: "border-emerald-500/30 text-emerald-500",
      failed: "border-red-500/30 text-red-500",
    };
    return (
      <span className={`text-[10px] font-bold uppercase px-2 py-0.5 border ${map[status] || "border-zinc-500/30 text-zinc-500"}`}>
        {status === "pending_payout" ? "pending" : status === "payout_requested" ? "requested" : status || "unknown"}
      </span>
    );
  };

  const { page: pendingPage, setPage: setPendingPage, totalPages: pendingTotalPages, paginated: pendingPaginated } = usePagination(subTab === "pending" ? payouts : [], 10);
  const { page: historyPage, setPage: setHistoryPage, totalPages: historyTotalPages, paginated: historyPaginated } = usePagination(subTab === "history" ? payouts : [], 10);

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="border border-white/10 bg-[#121214] p-5">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Total Issued</div>
            <div className="text-2xl font-black">${summary.total_issued?.amount?.toFixed(2) || "0.00"}</div>
            <div className="text-xs text-zinc-500 mt-1">{summary.total_issued?.count || 0} records</div>
          </div>
          <div className="border border-amber-500/20 bg-[#121214] p-5">
            <div className="text-[10px] uppercase tracking-widest text-amber-500 mb-2">Pending</div>
            <div className="text-2xl font-black text-amber-500">${summary.pending?.amount?.toFixed(2) || "0.00"}</div>
            <div className="text-xs text-zinc-500 mt-1">{summary.pending?.count || 0} awaiting</div>
          </div>
          <div className="border border-emerald-500/20 bg-[#121214] p-5">
            <div className="text-[10px] uppercase tracking-widest text-emerald-500 mb-2">Paid Out</div>
            <div className="text-2xl font-black text-emerald-500">${summary.paid?.amount?.toFixed(2) || "0.00"}</div>
            <div className="text-xs text-zinc-500 mt-1">{summary.paid?.count || 0} completed</div>
          </div>
          <div className="border border-red-500/20 bg-[#121214] p-5">
            <div className="text-[10px] uppercase tracking-widest text-red-500 mb-2">Failed</div>
            <div className="text-2xl font-black text-red-500">${summary.failed?.amount?.toFixed(2) || "0.00"}</div>
            <div className="text-xs text-zinc-500 mt-1">{summary.failed?.count || 0} rejected</div>
          </div>
        </div>
      )}

      {/* Sub-tab Navigation */}
      <div className="flex items-center justify-between border-b border-white/10 pb-3">
        <div className="flex gap-4">
          <button
            onClick={() => setSubTab("pending")}
            className={`text-xs uppercase tracking-widest font-bold pb-2 border-b-2 transition-all ${subTab === "pending" ? "border-[#FF3B30] text-white" : "border-transparent text-zinc-500 hover:text-zinc-300"}`}
          >
            Pending ({summary?.pending?.count || 0})
          </button>
          <button
            onClick={() => setSubTab("history")}
            className={`text-xs uppercase tracking-widest font-bold pb-2 border-b-2 transition-all ${subTab === "history" ? "border-[#FF3B30] text-white" : "border-transparent text-zinc-500 hover:text-zinc-300"}`}
          >
            History
          </button>
        </div>
        {subTab === "history" && (
          <button
            onClick={exportCSV}
            className="text-[10px] uppercase tracking-widest font-bold px-4 py-2 border border-white/10 text-zinc-400 hover:text-white hover:border-white/30 transition-all"
          >
            Export CSV
          </button>
        )}
      </div>

      {/* Loading State */}
      {loading && (
        <div className="p-12 text-center text-zinc-500 animate-pulse uppercase tracking-widest text-xs">Loading payouts...</div>
      )}

      {/* Empty State */}
      {!loading && payouts.length === 0 && (
        <div className="border border-dashed border-white/10 p-16 text-center text-zinc-600 uppercase tracking-widest text-xs">
          {subTab === "pending" ? "No pending payouts" : "No payout history yet"}
        </div>
      )}

      {/* Pending Table */}
      {!loading && payouts.length > 0 && subTab === "pending" && (
        <div className="border border-white/10 bg-[#121214] overflow-hidden overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-white/10 bg-white/[0.02] uppercase tracking-widest text-[10px] font-bold text-zinc-500">
                <th className="p-4">Referrer</th>
                <th className="p-4">Amount</th>
                <th className="p-4">Period</th>
                <th className="p-4">Plan</th>
                <th className="p-4">Status</th>
                <th className="p-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {pendingPaginated.map((p) => (
                <tr key={p.earning_id} className="hover:bg-white/[0.01] transition-colors">
                  <td className="p-4">
                    <div className="font-bold text-sm text-[#E2E8F0]">
                      {p.referrer_shop_name || "Unknown"}
                      {p._is_requested && (
                        <span className="ml-2 text-xs font-bold text-[#FFB800] border border-[#FFB800]/30 px-1.5 py-0.5 uppercase tracking-widest">Requested</span>
                      )}
                    </div>
                    <div className="text-xs text-zinc-500">{p.referrer_email || ""}</div>
                  </td>
                  <td className="p-4">
                    <div className="font-mono font-bold text-sm text-emerald-400">${Number(p.amount || 0).toFixed(2)}</div>
                  </td>
                  <td className="p-4 text-xs text-zinc-400 font-mono">
                    {p.period || p.earning_month || (p.created_at ? new Date(p.created_at).toLocaleDateString("en-US", { month: "short", year: "numeric" }) : "—")}
                  </td>
                  <td className="p-4">
                    <span className="text-[10px] font-bold uppercase px-2 py-0.5 border border-white/10 text-zinc-400">
                      {p.plan_tier || p.subscription_tier || "—"}
                    </span>
                  </td>
                  <td className="p-4">{statusBadge(p.payout_status || p.status)}</td>
                  <td className="p-4 text-right">
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => handleApprove(p.earning_id)}
                        disabled={actionLoading === p.earning_id}
                        className="flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold px-3 py-1.5 border border-emerald-500/30 text-emerald-500 hover:bg-emerald-500/10 transition-all disabled:opacity-50"
                      >
                        <CheckCircle2 className="w-3 h-3" />
                        {actionLoading === p.earning_id ? "..." : "Pay"}
                      </button>
                      <button
                        onClick={() => handleReject(p.earning_id)}
                        disabled={actionLoading === p.earning_id}
                        className="flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold px-3 py-1.5 border border-red-500/30 text-red-500 hover:bg-red-500/10 transition-all disabled:opacity-50"
                      >
                        <XCircle className="w-3 h-3" />
                        {actionLoading === p.earning_id ? "..." : "Reject"}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pagination page={pendingPage} totalPages={pendingTotalPages} setPage={setPendingPage} />
        </div>
      )}

      {/* History Table */}
      {!loading && payouts.length > 0 && subTab === "history" && (
        <div className="border border-white/10 bg-[#121214] overflow-hidden overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-white/10 bg-white/[0.02] uppercase tracking-widest text-[10px] font-bold text-zinc-500">
                <th className="p-4">Referrer</th>
                <th className="p-4">Amount</th>
                <th className="p-4">Status</th>
                <th className="p-4">Method</th>
                <th className="p-4">Note</th>
                <th className="p-4 text-right">Date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {historyPaginated.map((p, idx) => (
                <tr key={p.earning_id || idx} className="hover:bg-white/[0.01] transition-colors">
                  <td className="p-4">
                    <div className="font-bold text-sm text-[#E2E8F0]">{p.referrer_shop_name || "Unknown"}</div>
                    <div className="text-xs text-zinc-500">{p.referrer_email || ""}</div>
                  </td>
                  <td className="p-4">
                    <div className="font-mono font-bold text-sm">${Number(p.amount || 0).toFixed(2)}</div>
                  </td>
                  <td className="p-4">{statusBadge(p.payout_status || p.status)}</td>
                  <td className="p-4 text-xs text-zinc-400 uppercase tracking-wider">
                    {p.payout_method || "manual"}
                  </td>
                  <td className="p-4 text-xs text-zinc-500 max-w-[200px] truncate">
                    {p.payout_note || "—"}
                  </td>
                  <td className="p-4 text-right text-xs text-zinc-500 font-mono">
                    {(p.paid_at || p.failed_at || p.created_at) ? new Date(p.paid_at || p.failed_at || p.created_at).toLocaleDateString() : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pagination page={historyPage} totalPages={historyTotalPages} setPage={setHistoryPage} />
        </div>
      )}

      {/* Total count */}
      {!loading && payouts.length > 0 && (
        <div className="text-right text-[10px] uppercase tracking-widest text-zinc-600">
          Showing {payouts.length} of {total} records
        </div>
      )}
    </div>
  );
}


function SettingsTab() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Local editable state
  const [platformFee, setPlatformFee] = useState("");
  const [tiers, setTiers] = useState({});
  const [commissions, setCommissions] = useState({});
  const [hasChanges, setHasChanges] = useState(false);

  const loadSettings = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get("/admin/settings");
      setSettings(res.data);
      setPlatformFee(String(res.data.platform_fee_percent ?? ""));
      setTiers(res.data.subscription_tiers || {});
      setCommissions(res.data.referral_commissions || {});
      setHasChanges(false);
    } catch (e) {
      setError("Failed to load settings");
      console.error("Settings load error", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSettings();
  }, []);

  const handleTierPriceChange = (tier, value) => {
    setTiers((prev) => ({
      ...prev,
      [tier]: { ...prev[tier], price: value === "" ? "" : Number(value) },
    }));
    setHasChanges(true);
  };

  const handleCommissionChange = (tier, value) => {
    setCommissions((prev) => ({
      ...prev,
      [tier]: value === "" ? "" : Number(value),
    }));
    setHasChanges(true);
  };

  const handleFeeChange = (value) => {
    setPlatformFee(value);
    setHasChanges(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const body = {};
      const feeVal = parseFloat(platformFee);
      if (!isNaN(feeVal)) body.platform_fee_percent = feeVal;

      // Build subscription tiers payload
      const tierPayload = {};
      Object.entries(tiers).forEach(([key, val]) => {
        const price = typeof val === "object" ? val.price : val;
        if (price !== "" && !isNaN(Number(price))) {
          tierPayload[key] = { price: Number(price), label: (typeof val === "object" ? val.label : key.charAt(0).toUpperCase() + key.slice(1)) };
        }
      });
      if (Object.keys(tierPayload).length > 0) body.subscription_tiers = tierPayload;

      // Build referral commissions payload
      const commPayload = {};
      Object.entries(commissions).forEach(([key, val]) => {
        if (val !== "" && !isNaN(Number(val))) {
          commPayload[key] = Number(val);
        }
      });
      if (Object.keys(commPayload).length > 0) body.referral_commissions = commPayload;

      await api.post("/admin/settings", body);
      toast.success("Settings saved successfully");
      await loadSettings();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (settings) {
      setPlatformFee(String(settings.platform_fee_percent ?? ""));
      setTiers(settings.subscription_tiers || {});
      setCommissions(settings.referral_commissions || {});
      setHasChanges(false);
    }
  };

  if (loading) {
    return (
      <div className="p-12 text-center text-zinc-500 animate-pulse uppercase tracking-widest text-xs">
        Loading system settings...
      </div>
    );
  }

  if (error) {
    return (
      <div className="border border-dashed border-red-500/20 p-16 text-center space-y-4">
        <div className="text-red-500 uppercase tracking-widest text-xs font-bold">{error}</div>
        <button onClick={loadSettings} className="text-[10px] uppercase tracking-widest font-bold px-4 py-2 border border-white/10 text-zinc-400 hover:text-white hover:border-white/30 transition-all">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-xl font-black uppercase tracking-tight">Platform Economics</h2>
          <p className="text-xs text-zinc-500 mt-1">
            {settings?.updated_at ? `Last updated: ${new Date(settings.updated_at).toLocaleString()}` : "Default values active"}
          </p>
        </div>
        <div className="flex gap-3">
          {hasChanges && (
            <button
              onClick={handleReset}
              className="text-[10px] uppercase tracking-widest font-bold px-4 py-2 border border-white/10 text-zinc-400 hover:text-white hover:border-white/30 transition-all"
            >
              Discard
            </button>
          )}
          <button
            onClick={handleSave}
            disabled={saving || !hasChanges}
            className="text-[10px] uppercase tracking-widest font-bold px-6 py-2 bg-[#FF3B30] text-white hover:bg-[#FF3B30]/80 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </div>

      {/* Platform Fee */}
      <div className="border border-white/10 bg-[#121214] p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-1">Platform Fee</div>
            <div className="text-xs text-zinc-400">Commission percentage charged on each transaction</div>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="number"
              value={platformFee}
              onChange={(e) => handleFeeChange(e.target.value)}
              min="0"
              max="50"
              step="0.5"
              className="w-20 bg-zinc-900 border border-white/10 text-white text-sm px-3 py-2 text-right font-mono focus:outline-none focus:border-[#FF3B30]/50"
            />
            <span className="text-zinc-400 text-sm font-bold">%</span>
          </div>
        </div>
      </div>

      {/* Subscription Tiers */}
      <div className="border border-white/10 bg-[#121214] p-6 space-y-5">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-1">Subscription Tier Pricing</div>
          <div className="text-xs text-zinc-400">Monthly pricing for each subscription level</div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {Object.entries(tiers).map(([key, val]) => {
            const label = typeof val === "object" ? val.label : key.charAt(0).toUpperCase() + key.slice(1);
            const price = typeof val === "object" ? val.price : val;
            const tierColors = {
              free: "border-zinc-500/30",
              pro: "border-blue-500/30",
              premium: "border-purple-500/30",
              fleet: "border-amber-500/30",
              empire: "border-[#FF3B30]/30",
            };
            return (
              <div key={key} className={`border ${tierColors[key] || "border-white/10"} bg-zinc-900/50 p-4 space-y-3`}>
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold uppercase tracking-widest text-zinc-300">{label}</span>
                  <span className="text-[10px] font-mono text-zinc-600">{key}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-zinc-500 text-sm">$</span>
                  <input
                    type="number"
                    value={price}
                    onChange={(e) => handleTierPriceChange(key, e.target.value)}
                    min="0"
                    max="999"
                    step="0.01"
                    disabled={key === "free"}
                    className="flex-1 bg-zinc-800 border border-white/10 text-white text-sm px-3 py-2 font-mono focus:outline-none focus:border-[#FF3B30]/50 disabled:opacity-40 disabled:cursor-not-allowed"
                  />
                  <span className="text-zinc-600 text-[10px] uppercase">/mo</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Referral Commissions */}
      <div className="border border-white/10 bg-[#121214] p-6 space-y-5">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-1">Referral Commissions</div>
          <div className="text-xs text-zinc-400">Earnings paid to referrers per successful subscription signup</div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {Object.entries(commissions).map(([key, val]) => {
            const tierLabels = { pro: "Pro", premium: "Premium", fleet: "Fleet", empire: "Empire" };
            const tierColors = {
              pro: "text-blue-400",
              premium: "text-purple-400",
              fleet: "text-amber-400",
              empire: "text-[#FF3B30]",
            };
            return (
              <div key={key} className="border border-white/10 bg-zinc-900/50 p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <span className={`text-xs font-bold uppercase tracking-widest ${tierColors[key] || "text-zinc-300"}`}>
                    {tierLabels[key] || key}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-zinc-500 text-sm">$</span>
                  <input
                    type="number"
                    value={val}
                    onChange={(e) => handleCommissionChange(key, e.target.value)}
                    min="0"
                    max="200"
                    step="0.50"
                    className="flex-1 bg-zinc-800 border border-white/10 text-white text-sm px-3 py-2 font-mono focus:outline-none focus:border-[#FF3B30]/50"
                  />
                  <span className="text-zinc-600 text-[10px] uppercase">per ref</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Info Footer */}
      <div className="text-center text-[10px] uppercase tracking-widest text-zinc-600 py-4">
        Changes take effect immediately across the platform • No restart required
      </div>
    </div>
  );
}

function MessagesTab() {
  const [conversations, setConversations] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [thread, setThread] = useState(null);
  const [reply, setReply] = useState("");
  const [filter, setFilter] = useState("all");
  const [loadingConvos, setLoadingConvos] = useState(true);
  const [loadingThread, setLoadingThread] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    loadConversations();
  }, []);

  useEffect(() => {
    if (selectedId) loadThread(selectedId);
  }, [selectedId]);

  const loadConversations = async () => {
    try {
      const res = await api.get("/admin/support/conversations");
      setConversations(res.data.conversations || []);
    } catch (e) {
      toast.error("Failed to load conversations");
    } finally {
      setLoadingConvos(false);
    }
  };

  const loadThread = async (id) => {
    setLoadingThread(true);
    try {
      const res = await api.get(`/admin/support/messages/${id}`);
      setThread({
        messages: res.data.messages || [],
        shop_name: res.data.shop?.shop_name || res.data.shop?.legal_business_name || "Unknown",
        email: res.data.shop?.email || "",
        subject: "Support Thread",
        status: res.data.resolved ? "closed" : "open",
      });
    } catch (e) {
      toast.error("Failed to load conversation");
    } finally {
      setLoadingThread(false);
    }
  };

  const sendReply = async () => {
    if (!reply.trim() || !selectedId) return;
    setSending(true);
    try {
      await api.post(`/admin/support/messages/${selectedId}/reply`, { message: reply });
      setReply("");
      loadThread(selectedId);
      loadConversations();
    } catch (e) {
      toast.error("Failed to send reply");
    } finally {
      setSending(false);
    }
  };

  const toggleStatus = async () => {
    if (!thread) return;
    const newStatus = thread.status === "open" ? "closed" : "open";
    try {
      await api.patch(`/admin/support/messages/${selectedId}/resolve`);
      setThread({ ...thread, status: newStatus });
      loadConversations();
      toast.success(`Ticket ${newStatus === "closed" ? "closed" : "reopened"}`);
    } catch (e) {
      toast.error("Failed to update status");
    }
  };

  const filtered = conversations.filter((c) => {
    if (filter === "open") return !c.resolved;
    if (filter === "closed") return c.resolved;
    return true;
  });

  const { page, setPage, totalPages, paginated: paginatedConvos } = usePagination(filtered, 10);

  const formatTime = (ts) => {
    if (!ts) return "";
    const d = new Date(ts);
    const now = new Date();
    const diff = now - d;
    if (diff < 60000) return "now";
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h`;
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  return (
    <div className="flex border border-white/10 h-[600px]">
      {/* Left panel - conversation list */}
      <div className="w-1/3 border-r border-white/10 flex flex-col">
        {/* Filter tabs */}
        <div className="flex border-b border-white/10">
          {["all", "open", "closed"].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`flex-1 px-3 py-2 text-[10px] uppercase tracking-widest font-bold transition-colors ${filter === f ? "bg-[#FF3B30] text-white" : "text-zinc-500 hover:text-white hover:bg-white/5"}`}
            >
              {f}
            </button>
          ))}
        </div>
        {/* Conversation list */}
        <div className="flex-1 overflow-y-auto">
          {loadingConvos ? (
            <div className="p-8 text-center text-zinc-600 text-xs uppercase tracking-widest">Loading...</div>
          ) : filtered.length === 0 ? (
            <div className="p-8 text-center text-zinc-600 text-xs uppercase tracking-widest">No conversations</div>
          ) : (
            paginatedConvos.map((c) => (
              <button
                key={c.shop_id}
                onClick={() => setSelectedId(c.shop_id)}
                className={`w-full text-left p-4 border-b border-white/5 transition-colors ${selectedId === c.shop_id ? "bg-white/5" : "hover:bg-white/[0.02]"}`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-bold text-white truncate max-w-[60%]">{c.shop_name || "Unknown"}</span>
                  <span className="text-[10px] text-zinc-600 font-mono">{formatTime(c.last_message_at)}</span>
                </div>
                <div className="text-[11px] text-zinc-400 truncate mb-1">{c.subject || "No subject"}</div>
                <div className="flex items-center justify-between">
                  <span className="text-[10px] text-zinc-600 truncate max-w-[75%]">{c.last_message_preview || ""}</span>
                  {c.unread_admin > 0 && (
                    <span className="w-2 h-2 rounded-full bg-[#FF3B30] flex-shrink-0"></span>
                  )}
                </div>
              </button>
            ))
          )}
        </div>
        <Pagination page={page} totalPages={totalPages} setPage={setPage} />
      </div>

      {/* Right panel - message thread */}
      <div className="w-2/3 flex flex-col">
        {!selectedId ? (
          <div className="flex-1 flex items-center justify-center text-zinc-600 text-xs uppercase tracking-widest">
            Select a conversation
          </div>
        ) : loadingThread ? (
          <div className="flex-1 flex items-center justify-center text-zinc-600 text-xs uppercase tracking-widest">
            Loading...
          </div>
        ) : thread ? (
          <>
            {/* Thread header */}
            <div className="p-4 border-b border-white/10 flex items-center justify-between">
              <div>
                <div className="text-sm font-bold text-white">{thread.shop_name || "Unknown"}</div>
                <div className="text-[10px] text-zinc-500 font-mono">{thread.email}</div>
                <div className="text-[11px] text-zinc-400 mt-0.5">{thread.subject}</div>
              </div>
              <div className="flex items-center gap-3">
                <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-1 ${thread.status === "open" ? "text-green-400 bg-green-400/10" : "text-zinc-500 bg-zinc-500/10"}`}>
                  {thread.status}
                </span>
                <button
                  onClick={toggleStatus}
                  className="text-[10px] uppercase tracking-widest font-bold px-3 py-1 border border-white/10 text-zinc-400 hover:text-white hover:bg-white/5 transition-colors"
                >
                  {thread.status === "open" ? "Close" : "Reopen"}
                </button>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {(thread.messages || []).map((msg, i) => {
                const isAdmin = msg.sender === "admin";
                return (
                  <div key={i} className={`flex ${isAdmin ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[75%] px-4 py-3 rounded ${isAdmin ? "bg-zinc-700 text-white" : "bg-zinc-800 text-zinc-200"}`}>
                      <div className="text-sm whitespace-pre-wrap">{msg.content || msg.message}</div>
                      {msg.attachments && msg.attachments.length > 0 && (
                        <div className="mt-2 flex gap-2 flex-wrap">
                          {msg.attachments.map((att, idx) =>
                            typeof att === "string" && att.startsWith("data:image/") ? (
                              <img key={idx} src={att} alt="attachment" className="w-20 h-20 rounded object-cover border border-white/10" />
                            ) : (
                              <a
                                key={idx}
                                href={att}
                                download={att.startsWith("data:application/pdf") ? "document.pdf" : att.includes("wordprocessingml") ? "document.docx" : "document"}
                                className="flex items-center gap-2 px-3 py-2 rounded bg-black/20 border border-white/10 hover:bg-black/30 transition-colors"
                              >
                                <FileText className="w-4 h-4 flex-shrink-0" />
                                <span className="text-[11px] truncate max-w-[100px]">
                                  {att.startsWith("data:application/pdf") ? "document.pdf" : att.includes("wordprocessingml") ? "document.docx" : "document"}
                                </span>
                              </a>
                            )
                          )}
                        </div>
                      )}
                      <div className="text-[10px] text-zinc-500 mt-1 font-mono">
                        {msg.timestamp ? new Date(msg.timestamp).toLocaleString() : ""}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Reply box */}
            <div className="p-4 border-t border-white/10 flex gap-2">
              <input
                type="text"
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendReply(); } }}
                placeholder="Type a reply..."
                className="flex-1 bg-zinc-900 border border-white/10 text-white text-sm px-4 py-2 placeholder:text-zinc-600 focus:outline-none focus:border-[#FF3B30]/50"
              />
              <button
                onClick={sendReply}
                disabled={sending || !reply.trim()}
                className="px-5 py-2 bg-[#FF3B30] text-white text-xs uppercase tracking-widest font-bold hover:bg-[#FF3B30]/80 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {sending ? "..." : "Send"}
              </button>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}


function ApprovedShopsTab({ shops, onImpersonate, onReview }) {
  const sortedShops = [...(shops || [])].sort((a, b) => {
    const dateA = new Date(a.application_submitted_at || a.created_at || 0);
    const dateB = new Date(b.application_submitted_at || b.created_at || 0);
    return dateB - dateA;
  });
  const { page, setPage, totalPages, paginated } = usePagination(sortedShops, 10);

  if (!shops || shops.length === 0) {
    return (
      <div className="flex items-center justify-center py-20 text-zinc-500 text-sm">
        No approved shops yet.
      </div>
    );
  }

  const tierBadge = (tier) => {
    const t = (tier || "free").toLowerCase();
    if (t === "premium") return "bg-purple-500/10 text-purple-400";
    if (t === "pro") return "bg-[#FFB800]/10 text-[#FFB800]";
    return "bg-zinc-700/40 text-zinc-400";
  };

  return (
    <div className="border border-white/10 bg-[#121214] overflow-x-auto">
      <div className="px-4 py-3 border-b border-white/10 text-[10px] uppercase tracking-widest text-zinc-500 font-bold">
        Approved Shops ({shops.length})
      </div>
      <table className="w-full text-left">
        <thead>
          <tr className="border-b border-white/10 bg-white/[0.02] uppercase tracking-widest text-[10px] font-bold text-zinc-500">
            <th className="p-4">Shop Name</th>
            <th className="p-4">Email</th>
            <th className="p-4">Tier</th>
            <th className="p-4">Location</th>
            <th className="p-4">Approved</th>
            <th className="p-4 text-right">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-white/5">
          {paginated.map((s) => (
            <tr key={s.user_id} className="hover:bg-white/[0.01] transition-colors">
              <td className="p-4">
                <div className="font-bold text-sm">{s.shop_name || s.email}</div>
              </td>
              <td className="p-4 text-xs text-zinc-400">{s.email}</td>
              <td className="p-4">
                <span className={`text-[10px] font-bold uppercase tracking-tighter px-2 py-1 ${tierBadge(s.subscription_tier)}`}>
                  {s.subscription_tier || "free"}
                </span>
              </td>
              <td className="p-4 text-xs text-zinc-500 font-mono">
                {s.location?.lat && s.location?.lng
                  ? `${s.location.lat.toFixed(4)}, ${s.location.lng.toFixed(4)}`
                  : "Not set"}
              </td>
              <td className="p-4 text-xs text-zinc-400 font-mono">
                {s.application_submitted_at
                  ? new Date(s.application_submitted_at).toLocaleDateString()
                  : "—"}
              </td>
              <td className="p-4 text-right flex items-center justify-end gap-2">
                <button onClick={() => onImpersonate(s)} title="Impersonate" className="text-zinc-500 hover:text-white transition-colors">
                  <Eye className="w-4 h-4" />
                </button>
                <button onClick={() => onReview(s.user_id)} title="Review" className="text-zinc-500 hover:text-white transition-colors">
                  <ClipboardList className="w-4 h-4" />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <Pagination page={page} totalPages={totalPages} setPage={setPage} />
    </div>
  );
}

function ShopActionDialog({ state, onClose, onSuccess }) {
  const [loading, setLoading] = useState(false);
  if (!state) return null;

  const handleAction = async () => {
    setLoading(true);
    try {
      if (state.action === 'approve') {
        await api.post(`/admin/shops/${state.user.user_id}/approve`, { grant_founding: state.grant_founding ?? true });
        toast.success("Shop approved");
      } else if (state.action === 'reject') {
        await api.post(`/admin/shops/${state.user.user_id}/reject`);
        toast.success("Shop rejected");
      } else if (state.action === 'delete') {
        await api.delete(`/admin/users/${state.user.user_id}`);
        toast.success("User deleted");
      }
      onSuccess();
    } catch (e) {
      toast.error("Action failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={!!state} onOpenChange={onClose}>
      <DialogContent className="bg-[#121214] border-white/10 text-white rounded-none">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl uppercase font-black">Confirm {state.action}</DialogTitle>
        </DialogHeader>
        <div className="py-6">
          <p className="text-zinc-400">Are you sure you want to <span className="text-white font-bold">{state.action}</span> account <span className="text-white font-mono">{state.user.email}</span>?</p>
          {state.action === 'delete' && <p className="text-[#FF3B30] text-xs mt-4 uppercase font-bold tracking-widest">Warning: This action is irreversible.</p>}
        </div>
        <DialogFooter className="gap-3">
          <button onClick={onClose} className="btn-secondary px-6 py-2 uppercase text-[10px] font-bold">Cancel</button>
          <button onClick={handleAction} disabled={loading} className={`px-6 py-2 uppercase text-[10px] font-bold ${state.action === 'delete' ? 'bg-red-600 hover:bg-red-500' : 'bg-[#FF3B30] hover:bg-[#FF3B30]/80'} text-white`}>
            {loading ? "Processing..." : `Confirm ${state.action}`}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function DeactivatedTab() {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingNote, setEditingNote] = useState({});

  const fetchDeactivated = async () => {
    try {
      const res = await api.get("/admin/users?status=deactivated&limit=200");
      setAccounts(res.data || []);
    } catch {
      toast.error("Failed to load deactivated accounts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchDeactivated(); }, []);

  const handleReactivate = async (userId) => {
    if (!window.confirm("Reactivate this account?")) return;
    try {
      await api.post(`/admin/users/${userId}/status`, { action: "reactivate" });
      toast.success("Account reactivated");
      setAccounts((prev) => prev.filter((a) => a.user_id !== userId));
    } catch {
      toast.error("Failed to reactivate");
    }
  };

  const handleNoteChange = (userId, value) => {
    setEditingNote((prev) => ({ ...prev, [userId]: value }));
  };

  const saveNote = async (userId) => {
    const note = editingNote[userId] ?? "";
    try {
      await api.patch(`/admin/users/${userId}/deactivation-note`, { note });
      toast.success("Note saved");
      setAccounts((prev) =>
        prev.map((a) => (a.user_id === userId ? { ...a, deactivation_note: note } : a))
      );
    } catch {
      toast.error("Failed to save note");
    }
  };

  const formatDate = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) +
      " " + d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
  };

  if (loading) return <div className="text-center text-zinc-500 py-12">Loading...</div>;

  if (accounts.length === 0) {
    return <div className="text-center text-zinc-500 py-12">No deactivated accounts.</div>;
  }

  return (
    <div className="border border-white/10 bg-[#121214] overflow-x-auto">
      <table className="w-full text-left">
        <thead>
          <tr className="border-b border-white/10 bg-white/[0.02] uppercase tracking-widest text-[10px] font-bold text-zinc-500">
            <th className="px-4 py-3">Account #</th>
            <th className="px-4 py-3">Name</th>
            <th className="px-4 py-3">Email</th>
            <th className="px-4 py-3">Role</th>
            <th className="px-4 py-3">Deactivated On</th>
            <th className="px-4 py-3">Note</th>
            <th className="px-4 py-3"></th>
          </tr>
        </thead>
        <tbody>
          {accounts.map((u, idx) => (
            <tr key={u.user_id} className="border-b border-white/5 hover:bg-white/[0.02]">
              <td className="px-4 py-3 text-xs text-zinc-400 font-mono">{idx + 1}</td>
              <td className="px-4 py-3 text-sm text-white">{u.name || u.shop_name || "—"}</td>
              <td className="px-4 py-3 text-xs text-zinc-400">{u.email}</td>
              <td className="px-4 py-3 text-xs text-zinc-400 uppercase">{u.role || "—"}</td>
              <td className="px-4 py-3 text-xs text-zinc-400">{formatDate(u.deactivated_at)}</td>
              <td className="px-4 py-3">
                <div className="flex items-center gap-1">
                  <input
                    type="text"
                    value={editingNote[u.user_id] ?? u.deactivation_note ?? ""}
                    onChange={(e) => handleNoteChange(u.user_id, e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") saveNote(u.user_id); }}
                    placeholder="Add note..."
                    className="bg-transparent border border-white/10 px-2 py-1 text-xs text-white w-24 focus:border-[#FFB800] focus:outline-none transition-colors"
                  />
                  <button
                    onClick={() => saveNote(u.user_id)}
                    className="text-[#FFB800] hover:text-white text-sm px-1 transition-colors"
                    title="Save note"
                  >
                    ✓
                  </button>
                </div>
              </td>
              <td className="px-4 py-3">
                <button
                  onClick={() => handleReactivate(u.user_id)}
                  className="text-xs font-bold uppercase tracking-widest border border-green-500/30 text-green-400 hover:bg-green-500/10 px-3 py-1.5 transition-colors"
                >
                  Reactivate
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AccountSettingsTab() {
  const [pwForm, setPwForm] = useState({ current_password: "", new_password: "", confirm_password: "" });
  const [savingPw, setSavingPw] = useState(false);

  const handlePasswordChange = async () => {
    if (pwForm.new_password !== pwForm.confirm_password) {
      toast.error("New passwords don't match");
      return;
    }
    if (pwForm.new_password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    setSavingPw(true);
    try {
      await api.post("/auth/password", {
        current_password: pwForm.current_password,
        new_password: pwForm.new_password,
      });
      toast.success("Password changed successfully");
      setPwForm({ current_password: "", new_password: "", confirm_password: "" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to change password");
    } finally { setSavingPw(false); }
  };

  return (
    <div className="max-w-lg space-y-6">
      <p className="label-eyebrow text-zinc-500 mb-6">Change Password</p>
      <div className="space-y-4">
        <div>
          <label className="label-eyebrow text-zinc-600 mb-2 block text-xs">Current Password</label>
          <input
            type="password"
            value={pwForm.current_password}
            onChange={(e) => setPwForm({ ...pwForm, current_password: e.target.value })}
            className="input w-full"
            placeholder="••••••••"
          />
        </div>
        <div>
          <label className="label-eyebrow text-zinc-600 mb-2 block text-xs">New Password</label>
          <input
            type="password"
            value={pwForm.new_password}
            onChange={(e) => setPwForm({ ...pwForm, new_password: e.target.value })}
            className="input w-full"
            placeholder="Min. 8 characters"
          />
        </div>
        <div>
          <label className="label-eyebrow text-zinc-600 mb-2 block text-xs">Confirm New Password</label>
          <input
            type="password"
            value={pwForm.confirm_password}
            onChange={(e) => setPwForm({ ...pwForm, confirm_password: e.target.value })}
            className="input w-full"
            placeholder="••••••••"
          />
        </div>
        <button
          onClick={handlePasswordChange}
          disabled={savingPw}
          className="btn-primary text-sm"
        >
          {savingPw ? "Updating..." : "Update Password"}
        </button>
      </div>
    </div>
  );
}


/** shop-status-access-nov2: admin list of bookings / accepted jobs whose shop
 *  was suspended or rejected. Owners see "this shop is currently unavailable";
 *  admin follows up and marks each entry resolved (reinstating the shop
 *  resolves its entries automatically). */
function ShopAttentionPanel() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/admin/shops/attention").then((r) => setItems(r.data?.items || [])).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!items.length) return null;
  const resolve = async (id) => {
    try {
      await api.post(`/admin/shops/attention/${id}/resolve`);
      setItems((xs) => xs.filter((x) => x.attention_id !== id));
    } catch (e) {
      toast.error("Could not resolve");
    }
  };
  return (
    <div className="mb-10 border border-[#FFB800]/40 bg-[#FFB800]/5 p-5" data-testid="admin-shop-attention">
      <div className="text-sm font-black uppercase tracking-widest text-[#FFB800] mb-3">
        Needs attention: {items.length} booking(s)/job(s) with an unavailable shop
      </div>
      <div className="space-y-2">
        {items.map((it) => (
          <div key={it.attention_id} className="flex items-center justify-between gap-3 text-xs text-zinc-300 border-t border-white/10 pt-2">
            <span>
              {it.kind === "booking" ? "Booking" : "Accepted job"} {it.booking_id || it.issue_id} | {it.shop_name || it.shop_id} ({String(it.reason || "").replace("shop_", "")})
              {it.slot_start ? ` | ${new Date(it.slot_start).toLocaleString()}` : ""}
            </span>
            <button onClick={() => resolve(it.attention_id)} className="border border-white/20 px-2 py-1 uppercase tracking-widest hover:bg-white/5">Resolved</button>
          </div>
        ))}
      </div>
    </div>
  );
}
